import React, { useEffect, useState } from 'react';
import { 
  Dialog, 
  DialogTrigger, 
  DialogPortal, 
  DialogOverlay, 
  DialogContent, 
  DialogTitle, 
  DialogDescription, 
  DialogClose,
} from '@radix-ui/react-dialog';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@radix-ui/react-tabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { cn } from '~/helpers/utils';
import ToggleBooleanField from '~/components/forms/ToggleBooleanField';
import AmountField from '~/components/forms/Tools/CalculateMaximumMortgage/AmountField';
import SelectField from '~/components/forms/SelectField';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '~/components/ui/Button';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';

type IncomeCalculationProps = {
  onHandleCalculate: (value: number) => void;
};

const CalculationSchema = z.object({
  monthlyIncome: z.number().min(1, 'Vul een geldig inkomen in').optional(),
  holidayPayIncluded: z.boolean(),
  thirteenthMonthIncluded: z.boolean(),
  howManyYears: z.enum(['1', '2', '3']),
  incomeYearOne: z.number().min(1, 'Vul een geldig inkomen in').optional(),
  incomeYearTwo: z.number().min(1, 'Vul een geldig inkomen in').optional(),
  incomeYearThree: z.number().min(1, 'Vul een geldig inkomen in').optional(),
});
type CalculationFormValues = z.infer<typeof CalculationSchema>;

const IncomeCalculation: React.FC<IncomeCalculationProps> = ({ onHandleCalculate }) => {
  const [open, setOpen] = React.useState(false);
  const [activeTab, setActiveTab] = useState('regular');
  const [totalIncomeAmount, setTotalIncomeAmount] = useState(0);
  
  const form = useForm<CalculationFormValues>({
    resolver: zodResolver(CalculationSchema),
    defaultValues: {
      monthlyIncome: undefined,
      holidayPayIncluded: false,
      thirteenthMonthIncluded: false,
      howManyYears: '1',
      incomeYearOne: undefined,
      incomeYearTwo: undefined,
      incomeYearThree: undefined,
    }
  });

  const { watch } = form;
  
  const watchedValues = watch([
    'monthlyIncome',
    'holidayPayIncluded',
    'thirteenthMonthIncluded',
    'howManyYears',
    'incomeYearOne',
    'incomeYearTwo',
    'incomeYearThree',
  ]);
  const howManyYears = parseInt(watchedValues[3] ?? '1', 10);

  useEffect(() => {
    let result = 0;

    if (activeTab === 'regular') {
      const monthlyIncome = watchedValues[0];
      const holidayPayIncluded = watchedValues[1];
      const thirteenthMonthIncluded = watchedValues[2];

      if (monthlyIncome && !isNaN(monthlyIncome)) {
        let yearlyIncome = monthlyIncome * 12;
        if (holidayPayIncluded) yearlyIncome *= 1.08;
        if (thirteenthMonthIncluded) yearlyIncome += monthlyIncome;
        result = yearlyIncome;
      }
    } else {
      const incomeYearOne = watchedValues[4];
      const incomeYearTwo = watchedValues[5];
      const incomeYearThree = watchedValues[6];

      const incomes = [incomeYearOne, incomeYearTwo, incomeYearThree]
        .slice(3 - howManyYears)
        .filter((v): v is number => v !== undefined && !isNaN(v));

      if (incomes.length > 0) {
        result = incomes.reduce((sum, val) => sum + val, 0) / incomes.length;
      }
    }

    setTotalIncomeAmount(result);
  }, [watchedValues, activeTab]);
  
  const onSubmit = () => {
    onHandleCalculate(totalIncomeAmount);
    setOpen(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label="Open income calculator"
          className={cn(
            'group w-8 h-8 absolute top-1/2 -translate-y-1/2 right-4 flex items-center justify-center',
            'bg-white hover:bg-stone-200 transition-colors rounded-full shadow-sm',
          )}
        >
          <FontAwesomeIcon
            icon={faCalculator}
            className="text-lg text-stone-600 group-hover:text-blue-800"
          />
        </button>
      </DialogTrigger>

      <DialogPortal>
        <DialogOverlay
          className={cn(
            'fixed z-30 inset-0 bg-white/50 backdrop-blur-lg',
            'data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut',
          )}
        />
        <DialogContent
          className={cn(
            'fixed z-40 top-1/2 left-1/2 w-[90vw] max-w-[730px] -translate-x-1/2 -translate-y-1/2 max-h-[80vh] overflow-auto',
            'rounded-2xl bg-white p-6 shadow-lg focus:outline-none',
            'data-[state=open]:animate-slideUp data-[state=closed]:animate-slideDown',
          )}
        >
          <FormProvider {...form}>
            <div className="flex items-center justify-between mb-4">
              <DialogTitle className="text-2xl text-blue-900 font-bold font-display flex items-center gap-2">
                <FontAwesomeIcon icon={faCalculator} className="text-3xl text-blue-700" />
                Jaarinkomen berekenen
              </DialogTitle>
  
              <DialogClose asChild>
                <button
                  type="button"
                  aria-label="Close"
                  className="group flex items-center justify-center rounded-full hover:bg-stone-100 w-8 h-8 transition-colors"
                >
                  <FontAwesomeIcon icon={faXmark} className="text-stone-200 group-hover:text-black" />
                </button>
              </DialogClose>
            </div>
  
            <DialogDescription className="font-medium font-display mb-4">
              Bereken gemakkelijk jouw jaarinkomen met behulp van de volgende gegevens.
            </DialogDescription>
            
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="flex items-center gap-3 pb-7 border-b-2 border-stone-50">
                <TabsTrigger 
                  className={cn(
                    'relative cursor-pointer border rounded pl-10 pr-5 py-2 font-bold text-sm',
                    activeTab === 'regular' ? 'border-blue-500 bg-blue-25' : 'border-stone-200 bg-white',
                  )}
                  value="regular"
                >
                  <span
                    className={cn(
                      'block absolute bg-white left-5 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full',
                      activeTab === 'regular' ? 'border-4 border-blue-600' : 'border border-stone-400',
                    )}
                  />
                  In loondienst
                </TabsTrigger>
                <TabsTrigger
                  className={cn(
                    'relative cursor-pointer border rounded pl-10 pr-5 py-2 font-bold text-sm',
                    activeTab === 'business' ? 'border-blue-500 bg-blue-25' : 'border-stone-200 bg-white',
                  )}
                  value="business"
                >
                  <span
                    className={cn(
                      'block absolute bg-white left-5 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full',
                      activeTab === 'business' ? 'border-4 border-blue-600' : 'border border-stone-400',
                    )}
                  />
                  Ondernemer / ZZP’er
                </TabsTrigger>  
              </TabsList>
              
              <TabsContent value="regular">
                <div className="py-7 border-b-2 border-stone-50">
                  <AmountField 
                    name="monthlyIncome"
                    label="Wat is je bruto salaris?"
                  />
                  <ToggleBooleanField
                    name="holidayPayIncluded"
                    label="Ontvang je vakantiegeld?"
                  />
                  <ToggleBooleanField
                    name="thirteenthMonthIncluded"
                    label="Ontvang je een 13e maand?"
                  />
                </div>
              </TabsContent>
              <TabsContent value="business">
                <div className="py-7 border-b-2 border-stone-50">
                  <div className="pb-4">
                    <SelectField
                      name="howManyYears"
                      label="Hoe lang ben je al ondernemer?"
                      options={[
                        { value: '1', label: '1 jaar of minder' },
                        { value: '2', label: '2 jaar' },
                        { value: '3', label: '3 jaar of langer' },
                      ]}
                    />
                  </div>
                  <fieldset>
                    <legend className="form-label">Wat was je fiscale winst?</legend>
                    <div>
                      {howManyYears > 2 && (
                        <AmountField
                          name="incomeYearOne"
                          description={String(new Date().getFullYear() - 3)}
                          descriptionClass="mb-0"
                        />
                      )}
                      {howManyYears > 1 && (
                        <AmountField
                          name="incomeYearTwo"
                          description={String(new Date().getFullYear() - 2)}
                          descriptionClass="mb-0"
                        />
                      )}
                      <AmountField
                        name="incomeYearThree"
                        description={String(new Date().getFullYear() - 1)}
                        descriptionClass="mb-0"
                      />
                    </div>
                  </fieldset>
                </div>
              </TabsContent>
              <div className="pt-7">
                <div className="flex items-center justify-between">
                  <div className="form-control">
                    <span className="form-label">
                      Jouw bruto jaarinkomen
                    </span>
                    <div className="flex gap-2 text-stone-300 text-xl">
                      <span>&euro;</span>
                      <span className="font-bold">{totalIncomeAmount.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}</span>
                    </div>
                  </div>
                  
                  <div>
                    <Button 
                      type="button" 
                      onClick={form.handleSubmit(onSubmit)}
                      variant="default"
                    >
                      Invullen
                      <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                  </div>
                </div>
              </div>
            </Tabs>
          </FormProvider>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};

export default IncomeCalculation;